import type { RefObject } from 'react';
import { forwardRef, useEffect } from 'react';

/*
 * "Instalação simples" live vitals (design app.js §4 `initVitals`). The client capture is a flat image, so an
 * SVG laid over it in the capture's own pixel space (2034×1163) hides the CPU/GPU gauges, temperatures and
 * RAM read-outs under patches in the capture's colours and redraws them live: every ~1–1.5 s the values drift
 * to a new plausible reading with a short eased move; temperatures trail their load; the window's "Conectado"
 * pill reads 2–5 ms. Built only once the capture has loaded, runs only while it is on screen and the tab is
 * visible; reduced motion leaves the image as shot. Decorative: aria-hidden.
 */

interface Gauge {
  id: 'cpu' | 'gpu';
  cx: number;
  cy: number;
  arc: string;
  value: number;
  temp: number;
  card: string;
  tempX: number;
  tip: number;
  markY: number;
  disc: string[];
  bar: string[];
}

const GAUGES: Gauge[] = [
  { id: 'cpu', cx: 474.4, cy: 988.3, arc: '#aef55c', value: 6, temp: 42, card: '#16191b', tempX: 661.5, tip: 696.4, markY: 958, disc: ['938:#354728', '946:#374828', '954:#334426', '962:#2f3e25', '970:#2b3924', '978:#283422', '986:#242e20', '994:#20291f'], bar: ['946:#71d700', '952:#5fd800', '958:#4cd100', '964:#4cce18', '970:#4dd030'] },
  { id: 'gpu', cx: 1053.3, cy: 988.1, arc: '#53dc00', value: 16, temp: 34, card: '#181c1e', tempX: 1240.5, tip: 1275.4, markY: 973.5, disc: ['938:#234416', '946:#244516', '954:#214015', '962:#1f3a16', '970:#1e3617', '978:#1d3117', '986:#1b2c18', '994:#1a2819'], bar: ['961:#51db0e', '967:#4ed425', '973:#49c739', '979:#48c450', '985:#49c65d'] },
];
const RADIUS = 79.8;
const TRACK = '#3b4249';
const PX_PER_C = 1.875;
const PILL_OFF = [73, 80, 87] as const;
const PILL_ON = ['#56dc00', '#60db00', '#69da00', '#73d900', '#7dd800', '#87d700', '#91d600', '#9ad500', '#a4d400', '#aed300'].map((hex) => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)] as const);
const RAM0 = 55;
const PILL_X = 1381.6;
const PILL_DX = 18.78;
const PILL_INDEXES = Array.from({ length: 10 }, (_unused, index) => index);

function point(gauge: Gauge, degrees: number) {
  const angle = (degrees * Math.PI) / 180;
  return `${(gauge.cx + RADIUS * Math.cos(angle)).toFixed(2)} ${(gauge.cy + RADIUS * Math.sin(angle)).toFixed(2)}`;
}
function arcPath(gauge: Gauge) {
  return `M${point(gauge, 150)} A${RADIUS} ${RADIUS} 0 1 1 ${point(gauge, 30)}`;
}
function stopY(stop: string) {
  return Number(stop.split(':')[0]);
}

function Gradient({ id, stops, y1, y2 }: { id: string; stops: string[]; y1: number; y2: number }) {
  return (
    <linearGradient id={id} gradientUnits='userSpaceOnUse' x1='0' y1={y1} x2='0' y2={y2}>
      {stops.map((stop) => {
        const [offset, color] = stop.split(':');
        return <stop key={stop} offset={((Number(offset) - y1) / (y2 - y1)).toFixed(3)} stopColor={color} />;
      })}
    </linearGradient>
  );
}

export const VitalsSvg = forwardRef<SVGSVGElement>(function VitalsSvg(_props, ref) {
  return (
    <svg ref={ref} className='v1-app__vitals' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 2034 1163' aria-hidden='true' focusable='false'>
      <defs>
        {GAUGES.map((gauge) => (
          <g key={gauge.id}>
            <Gradient id={`apv-${gauge.id}-disc`} stops={gauge.disc} y1={938} y2={994} />
            <Gradient id={`apv-${gauge.id}-bar`} stops={gauge.bar} y1={stopY(gauge.bar[0] ?? '0')} y2={stopY(gauge.bar[gauge.bar.length - 1] ?? '1')} />
          </g>
        ))}
        {/* Patches get soft edges; sRGB keeps the dark flat fill exact (linearRGB would crush it). */}
        <filter id='apv-soft' x='-20%' y='-30%' width='140%' height='160%' colorInterpolationFilters='sRGB'>
          <feGaussianBlur stdDeviation='2' />
        </filter>
        <filter id='apv-soft1' x='-20%' y='-30%' width='140%' height='160%' colorInterpolationFilters='sRGB'>
          <feGaussianBlur stdDeviation='1.2' />
        </filter>
      </defs>
      {GAUGES.map((gauge) => {
        const path = arcPath(gauge);
        const { markY } = gauge;
        return (
          <g key={gauge.id}>
            <path d={path} fill='none' stroke={TRACK} strokeWidth='12.4' />
            <path data-k={`${gauge.id}Glow`} d={path} pathLength={100} fill='none' stroke={gauge.arc} strokeOpacity='.18' strokeWidth='18' strokeLinecap='round' />
            <path data-k={`${gauge.id}Arc`} d={path} pathLength={100} fill='none' stroke={gauge.arc} strokeWidth='10.8' strokeLinecap='round' />
            <rect x={(gauge.cx - 45).toFixed(1)} y='941' width='84' height='51' fill={`url(#apv-${gauge.id}-disc)`} filter='url(#apv-soft1)' />
            <text data-k={`${gauge.id}Use`} className='apv-use' x={(gauge.cx - 5).toFixed(1)} y='988.5' textAnchor='middle' fill={gauge.arc}>
              {gauge.value}
            </text>
            <rect x={gauge.tempX - 72} y='836' width='75' height='27' fill={gauge.card} filter='url(#apv-soft)' />
            <text data-k={`${gauge.id}Temp`} className='apv-temp' x={gauge.tempX} y='861' textAnchor='end' fill='#adb5bd'>
              {`${gauge.temp}°C`}
            </text>
            <rect x={(gauge.tip + 2.6).toFixed(1)} y={markY - 13} width='17' height='26' fill={gauge.card} />
            <rect x={(gauge.tip - 1.4).toFixed(1)} y={markY - 12} width='4' height='24' fill={`url(#apv-${gauge.id}-bar)`} />
            <g data-k={`${gauge.id}Mark`}>
              <path d={`M${(gauge.tip + 0.8).toFixed(1)} ${markY}l10.6 -8.4v16.8z`} fill='#fff' stroke='#fff' strokeWidth='2' strokeLinejoin='round' className='apv-mark' />
            </g>
          </g>
        );
      })}
      <rect x='1745' y='912' width='51' height='37' fill='#181d1e' filter='url(#apv-soft)' />
      <text data-k='ramGb' className='apv-gb' x='1791' y='945.5' textAnchor='end' fill='#c4ff9c'>
        {RAM0}
      </text>
      <rect x='1376' y='948' width='186' height='30' fill='#171b1d' />
      {PILL_INDEXES.map((index) => (
        <rect key={index} data-k={`pill${index}`} x={(PILL_X + PILL_DX * index).toFixed(2)} y='952.6' width='9.2' height='21' rx='4.6' fill={`rgb(${PILL_OFF.join(',')})`} />
      ))}
    </svg>
  );
});

type VitalKey = 'cpu' | 'cpuT' | 'gpu' | 'gpuT' | 'ram';
type Vitals = Record<VitalKey, number>;
const KEYS: VitalKey[] = ['cpu', 'cpuT', 'gpu', 'gpuT', 'ram'];
const BASE: Vitals = { cpu: 6, cpuT: 42, gpu: 16, gpuT: 34, ram: RAM0 };
const DURATION = 700;

interface AppVitalsOptions {
  imgRef: RefObject<HTMLImageElement>;
  svgRef: RefObject<SVGSVGElement>;
  pingRef: RefObject<HTMLElement>;
  built: boolean;
  onBuild: () => void;
}

export function useAppVitals({ imgRef, svgRef, pingRef, built, onBuild }: AppVitalsOptions): void {
  useEffect(() => {
    const img = imgRef.current;
    if (!img || !('IntersectionObserver' in window) || typeof window.requestAnimationFrame !== 'function') return undefined;
    const motion = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    const reduced = () => Boolean(motion?.matches);

    const elements: Record<string, { element: Element; last: string | null }> = {};
    svgRef.current?.querySelectorAll('[data-k]').forEach((element) => {
      elements[element.getAttribute('data-k') ?? ''] = { element, last: null };
    });
    const set = (key: string, attribute: string, value: string) => {
      const entry = elements[key];
      if (!entry || entry.last === value) return;
      entry.last = value;
      if (attribute === 'text') entry.element.textContent = value;
      else entry.element.setAttribute(attribute, value);
    };

    const copy = (source: Vitals): Vitals => ({ ...source });
    let current = copy(BASE);
    let from = copy(BASE);
    const to = copy(BASE);

    const paint = () => {
      GAUGES.forEach((gauge) => {
        const value = current[gauge.id];
        const temp = current[`${gauge.id}T`];
        const dash = `${value.toFixed(2)} 200`;
        set(`${gauge.id}Arc`, 'stroke-dasharray', dash);
        set(`${gauge.id}Glow`, 'stroke-dasharray', dash);
        set(`${gauge.id}Use`, 'text', String(Math.round(value)));
        set(`${gauge.id}Temp`, 'text', `${Math.round(temp)}°C`);
        set(`${gauge.id}Mark`, 'transform', `translate(0 ${(-(temp - gauge.temp) * PX_PER_C).toFixed(2)})`);
      });
      set('ramGb', 'text', String(Math.round(current.ram)));
      const lit = (current.ram / 128) * 20 + 0.4; // +.4: the shot's 55 GB shows nine pills fully lit
      PILL_INDEXES.forEach((index) => {
        const amount = Math.max(0, Math.min(1, lit - index));
        const on = PILL_ON[index] ?? PILL_ON[0] ?? [0, 0, 0];
        const channels = PILL_OFF.map((off, channel) => Math.round(off + ((on[channel] ?? off) - off) * amount));
        set(`pill${index}`, 'fill', `rgb(${channels.join(',')})`);
      });
    };
    const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));
    const jitter = (amplitude: number) => (Math.random() - 0.5) * 2 * amplitude;
    const pick = () => {
      // loads wander around a gaming-idle mean with the odd burst; heat trails its load (lagged)
      const cpu = Math.random() < 0.12 ? 15 + Math.random() * 7 : current.cpu + jitter(5) + (9 - current.cpu) * 0.3;
      const gpu = Math.random() < 0.12 ? 30 + Math.random() * 8 : current.gpu + jitter(7) + (22 - current.gpu) * 0.3;
      to.cpu = clamp(cpu, 4, 22);
      to.gpu = clamp(gpu, 12, 38);
      to.cpuT = clamp(current.cpuT + (40 + ((to.cpu - 4) / 18) * 7 + jitter(0.6) - current.cpuT) * 0.45, 40, 47);
      to.gpuT = clamp(current.gpuT + (33 + ((to.gpu - 12) / 26) * 6 + jitter(0.5) - current.gpuT) * 0.45, 33, 39);
      to.ram = clamp(current.ram + jitter(1.1) + (56 - current.ram) * 0.15, 52, 60);
      from = copy(current);
      const ping = pingRef.current;
      if (ping) {
        const ms = `${2 + Math.floor(Math.random() * 4)} ms`;
        if (ping.textContent !== ms) ping.textContent = ms;
      }
    };

    let raf = 0;
    let timer = 0;
    let startedAt = 0;
    let running = false;
    let inView = false;
    const ease = (progress: number) => (progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2);
    const frameStep = (now: number) => {
      if (!startedAt) startedAt = now;
      const progress = Math.min(1, (now - startedAt) / DURATION);
      const eased = ease(progress);
      KEYS.forEach((key) => {
        current[key] = from[key] + (to[key] - from[key]) * eased;
      });
      paint();
      raf = progress < 1 ? window.requestAnimationFrame(frameStep) : 0;
    };
    const step = () => {
      pick();
      startedAt = 0;
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(frameStep);
      timer = window.setTimeout(step, 1000 + Math.random() * 500);
    };
    const stop = () => {
      running = false;
      if (raf) window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      raf = 0;
      timer = 0;
    };
    const sync = () => {
      const go = inView && !document.hidden && !reduced() && img.complete && img.naturalWidth > 0;
      if (go && !built) {
        onBuild();
        return;
      }
      if (!built) return;
      if (go && !running) {
        running = true;
        timer = window.setTimeout(step, 400);
      } else if (!go && running) stop();
      if (reduced()) {
        stop();
        current = copy(BASE);
        paint();
      }
    };

    if (built) paint();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
        });
        sync();
      },
      { threshold: 0.15 }
    );
    observer.observe(img);
    document.addEventListener('visibilitychange', sync);
    img.addEventListener('load', sync);
    motion?.addEventListener('change', sync);

    return () => {
      stop();
      observer.disconnect();
      document.removeEventListener('visibilitychange', sync);
      img.removeEventListener('load', sync);
      motion?.removeEventListener('change', sync);
    };
  }, [imgRef, svgRef, pingRef, built, onBuild]);
}
